<script setup lang="ts">
  import { computed } from 'vue';
  import { FilterSortAndDelete, SearchNoResults } from '@/shared/ui';
  import DirectoriesTree from './DirectoriesTree.vue';
  import DirectoriesActionsPanel from './DirectoriesActionsPanel.vue';
  import DirectoriesEmptyState from './DirectoriesEmptyState.vue';
  import DirectoriesLegend from './DirectoriesLegend.vue';
  import DirectoriesPageHeader from './DirectoriesPageHeader.vue';
  import { DEFAULT_DIRECTORY_ICON_ID } from '@/shared/ui/module-icons';
  import { ORDER_OPTIONS } from '../config/directoriesListing';
  import { useDirectoriesPage } from '../model/useDirectoriesPage';
  import { useDirectoriesModals } from '../model/useDirectoriesModals';

  const {
    searchTerm,
    searchCriterion,
    currentSort,
    currentOrder,
    expandedIds,
    filteredTree,
    shouldShowSearchNoResults,
    selectedNode,
    selectedNodeDetails,
    selectedNodePermissions,
    isLoadingPermissions,
    canSaveConfigure,
    configureTitleError,
    actionsPanelTab,
    canCreateModule,
    canViewPermissionsTab,
    canToggleSuperadmin,
    selectedNodeIds,
    dragOverNodeId,
    getDirectoryKey,
    getDirectoryChildren,
    selectNode,
    onNodeDragStart,
    onNodeDragOver,
    onNodeDragLeave,
    onNodeDrop,
    onNodeDragEnd,
    handleSearch,
    handleSortUpdate,
    setActionsPanelTab,
    updateDraftTitle,
    updateDraftIconId,
    updateDraftArchived,
    toggleSuperadmin,
    togglePermissionPosition,
    togglePermissionFunctionalActivity,
    togglePositionsSelectAll,
    toggleFunctionalActivitiesSelectAll,
    applyPositions,
    applyFunctionalActivities,
    clearPositions,
    clearFunctionalActivities,
    canApplyPositions,
    canClearPositions,
    canApplyFunctionalActivities,
    canClearFunctionalActivities,
    copyPermissions,
    pastePermissions,
    canPastePermissions,
    savePanel,
    handleAddChild,
    selectedCount,
    isDeleteButtonVisible,
    deleteLockMessage,
    deleteModalTitle,
    deleteModalDescription,
    isDeletingSelection,
    confirmDeleteSelection,
    hasModules,
    isCreateModuleSubmitting,
    createModuleTitleError,
    handleCreateModuleSubmit,
    isCreateFolderSubmitting,
    createFolderTitleError,
    handleCreateFolderSubmit,
    isAddRecordSubmitting,
    handleAddRecordSubmit,
    isAddDocumentSubmitting,
    handleAddDocumentSubmit,
    eligibleDocumentsSearchUrl,
    eligibleMatricesSearchUrl,
  } = useDirectoriesPage();

  const {
    openCreateModuleModal,
    openCreateFolderModal,
    openAddRecordModal,
    openAddDocumentModal,
    openDeleteConfirmationModal,
    openPastePermissionsModal,
  } = useDirectoriesModals({
    selectedNode,
    isCreateModuleSubmitting,
    createModuleTitleError,
    handleCreateModuleSubmit,
    isCreateFolderSubmitting,
    createFolderTitleError,
    handleCreateFolderSubmit,
    handleAddChild,
    isAddRecordSubmitting,
    handleAddRecordSubmit,
    isAddDocumentSubmitting,
    handleAddDocumentSubmit,
    eligibleDocumentsSearchUrl,
    eligibleMatricesSearchUrl,
    selectedNodeIds,
    deleteModalTitle,
    deleteModalDescription,
    isDeletingSelection,
    confirmDeleteSelection,
    canPastePermissions,
    pastePermissions,
  });

  const onExpandedIdsUpdate = (ids: string[]) => {
    expandedIds.value = ids;
  };

  const iconModelValue = computed(
    () => selectedNodeDetails.value?.iconId ?? DEFAULT_DIRECTORY_ICON_ID
  );

  const panelDetails = computed(() => {
    if (!selectedNodeDetails.value) {
      return {
        title: '',
        createdAtLabel: '-',
        createdBy: '-',
        savedInDirectoryBy: '-',
      };
    }
    return selectedNodeDetails.value;
  });
</script>

<template>
  <div data-testid="directories-page" class="flex h-full min-h-0">
    <div
      data-testid="directories-page-body"
      class="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto px-6 pb-6"
    >
      <DirectoriesPageHeader
        v-model:search-criterion="searchCriterion"
        :search-term="searchTerm"
        :can-create-module="canCreateModule"
        @create-module="openCreateModuleModal"
        @search="handleSearch"
      />

      <template v-if="hasModules">
        <DirectoriesLegend class="mb-4 shrink-0" />

        <div
          data-testid="directories-tree-column"
          class="flex shrink-0 flex-col rounded-lg border border-gray-02 bg-white-01"
        >
          <div
            data-testid="directories-listing-toolbar"
            class="flex shrink-0 flex-col gap-4 px-4 pt-4 pb-2 lg:flex-row lg:items-center lg:justify-between"
          >
            <span class="text-base font-medium text-[var(--theme-secondary-ink)]">MÓDULOS</span>
            <div class="flex flex-wrap items-center justify-end gap-3">
              <FilterSortAndDelete
                :order-options="ORDER_OPTIONS"
                :current-sort="currentSort"
                :current-order="currentOrder"
                :has-filter="false"
                :has-delete="false"
                @update:sort="handleSortUpdate"
              />
            </div>
          </div>

          <div data-testid="directories-tree-scroll" class="min-w-0">
            <SearchNoResults
              v-if="shouldShowSearchNoResults"
              data-testid="directories-search-no-results"
            />
            <DirectoriesTree
              v-else
              :items="filteredTree"
              :expanded-ids="expandedIds"
              :selected-node-id="selectedNode?.id ?? ''"
              :selected-node-ids="selectedNodeIds"
              :drag-over-node-id="dragOverNodeId"
              :get-key="getDirectoryKey"
              :get-children="getDirectoryChildren"
              @update:expanded-ids="onExpandedIdsUpdate"
              @select="selectNode"
              @node-dragstart="onNodeDragStart"
              @node-dragover="onNodeDragOver"
              @node-dragleave="onNodeDragLeave"
              @node-drop="onNodeDrop"
              @node-dragend="onNodeDragEnd"
            />
          </div>
        </div>
      </template>

      <div
        v-else
        data-testid="directories-listing-empty"
        class="flex min-h-0 flex-1 items-center justify-center"
      >
        <DirectoriesEmptyState />
      </div>
    </div>

    <DirectoriesActionsPanel
      v-if="hasModules"
      class="h-full min-h-0"
      :has-selection="!!selectedNode || selectedCount > 1"
      :node="selectedNode ?? undefined"
      :active-tab="actionsPanelTab"
      :details="panelDetails ?? undefined"
      :permissions="selectedNodePermissions"
      :is-loading-permissions="isLoadingPermissions"
      :can-view-permissions-tab="canViewPermissionsTab"
      :can-toggle-superadmin="canToggleSuperadmin"
      :can-apply-positions="canApplyPositions"
      :can-clear-positions="canClearPositions"
      :can-apply-functional-activities="canApplyFunctionalActivities"
      :can-clear-functional-activities="canClearFunctionalActivities"
      :can-paste-permissions="canPastePermissions"
      :icon-model-value="iconModelValue"
      :selected-count="selectedCount"
      :is-delete-button-visible="isDeleteButtonVisible"
      :delete-lock-message="deleteLockMessage"
      :is-save-disabled="!canSaveConfigure"
      :title-error="configureTitleError"
      @update:active-tab="setActionsPanelTab"
      @update:title="updateDraftTitle"
      @update:icon-id="updateDraftIconId"
      @update:archived="updateDraftArchived"
      @toggle-superadmin="toggleSuperadmin"
      @toggle-position="togglePermissionPosition"
      @toggle-functional-activity="togglePermissionFunctionalActivity"
      @toggle-positions-select-all="togglePositionsSelectAll"
      @toggle-functional-activities-select-all="toggleFunctionalActivitiesSelectAll"
      @apply-positions="applyPositions"
      @apply-functional-activities="applyFunctionalActivities"
      @clear-positions="clearPositions"
      @clear-functional-activities="clearFunctionalActivities"
      @copy-permissions="copyPermissions"
      @paste-permissions="openPastePermissionsModal"
      @save="savePanel"
      @add-child="openCreateFolderModal"
      @add-record="openAddRecordModal"
      @add-document="openAddDocumentModal"
      @delete="openDeleteConfirmationModal"
    />
  </div>
</template>
